import React, { useState, useRef, useCallback } from 'react';

const DEFAULT_SECTION_ORDER = ['greeting', 'travel_data', 'photo', 'why', 'inclusions', 'closing', 'cta', 'ai'];

function MoveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" style={{ display: 'block' }}>
      <polygon points="7,1 5,3.5 9,3.5" />
      <polygon points="7,13 9,10.5 5,10.5" />
      <polygon points="1,7 3.5,5 3.5,9" />
      <polygon points="13,7 10.5,9 10.5,5" />
      <rect x="5.5" y="5.5" width="3" height="3" />
    </svg>
  );
}

function DropIndicator() {
  return (
    <div style={{
      height: 4, margin: '3px 0', borderRadius: 4,
      border: '2px dashed #1a3c5e', background: 'rgba(26,60,94,0.08)',
      pointerEvents: 'none',
    }} />
  );
}

function PhotoUploadSection({ url, onChange }) {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const readFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = e => onChange(e.target.result);
    reader.readAsDataURL(file);
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    readFile(file);
  };

  return (
    <div className="p-4 space-y-2">
      <label className="label text-xs">Foto URL</label>
      <input className="input text-sm" value={url.startsWith('data:') ? '' : url}
        onChange={e => onChange(e.target.value)}
        placeholder="https://..." />
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-1 py-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors text-sm ${dragging ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500'}`}
      >
        <span style={{ fontSize: 20 }}>🖼️</span>
        <span>Sleep een screenshot hier, of <span className="underline">kies bestand</span></span>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={e => readFile(e.target.files[0])} />
      </div>
      {url && (
        <div className="relative">
          <img src={url} alt="Preview"
            className="w-full h-28 object-cover rounded-lg"
            onError={e => { e.target.style.display = 'none'; }} />
          <button onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-black/70">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function RegenerateBtn({ section, regenSection, onRegenerate }) {
  if (!onRegenerate) return null;
  const loading = regenSection === section;
  return (
    <button
      type="button"
      onClick={() => onRegenerate(section)}
      disabled={!!regenSection}
      title="Hergeneer deze sectie"
      className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors flex items-center gap-1"
    >
      {loading
        ? <span className="spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} />
        : <span style={{ fontSize: 13 }}>↺</span>
      }
      <span>{loading ? 'Bezig…' : 'Hergeneer'}</span>
    </button>
  );
}

function SectionWrap({ title, mandatory, visible, onToggle, children, section, regenSection, onRegenerate, dragHandleProps }) {
  return (
    <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm" style={{ background: '#fff' }}>
      <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border-b-2 border-gray-200">
        <div className="flex items-center gap-2">
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              title="Versleep om te herordenen"
              style={{
                cursor: 'grab', color: '#9ca3af', display: 'flex', alignItems: 'center',
                padding: '2px 4px', borderRadius: 3, userSelect: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#1a3c5e'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
            >
              <MoveIcon />
            </div>
          )}
          <span className="text-sm font-medium text-gray-800">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {section && (
            <RegenerateBtn section={section} regenSection={regenSection} onRegenerate={onRegenerate} />
          )}
          {mandatory ? (
            <span className="text-xs text-gray-400">Verplicht</span>
          ) : (
            <button
              type="button"
              onClick={onToggle}
              className={`text-xs px-2.5 py-0.5 rounded-full transition-colors ${
                visible
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
              }`}
            >
              {visible ? 'Zichtbaar' : 'Verborgen'}
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function AiEditPanel({ onAiEdit }) {
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const apply = async () => {
    if (!instruction.trim() || busy) return;
    setBusy(true); setMsg('');
    try {
      await onAiEdit(instruction.trim());
      setMsg('Toegepast');
      setInstruction('');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Fout: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm" style={{ background: '#fff' }}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b-2 border-gray-200">
        <span className="text-sm font-medium text-gray-800">AI assistent</span>
        <span className="text-xs text-gray-400">Bewerk tekst met een instructie</span>
      </div>
      <div className="p-4 space-y-2.5">
        <textarea
          className="input text-sm min-h-[72px] resize-none"
          placeholder='Bijv. "Maak de aanhef formeler" of "Voeg een zin toe over het strand"'
          value={instruction}
          onChange={e => setInstruction(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) apply(); }}
          disabled={busy}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{msg}</span>
          <button
            type="button"
            onClick={apply}
            disabled={!instruction.trim() || busy}
            className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
          >
            {busy ? <span className="spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} /> : 'Toepassen'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProposalEditorV2({ proposal_v2, onChange, onRegenerate, regenSection, onAiEdit }) {
  const [dragKey, setDragKey] = useState(null);
  const [dropInsertPos, setDropInsertPos] = useState(null);
  const dragNodeRef = useRef(null);

  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = proposal_v2?.section_order;
    if (saved && Array.isArray(saved)) {
      // ensure new keys present
      const missing = DEFAULT_SECTION_ORDER.filter(k => !saved.includes(k));
      return [...saved, ...missing];
    }
    return [...DEFAULT_SECTION_ORDER];
  });

  if (!proposal_v2) return null;

  const {
    travel_data: td = {},
    persuasive: p   = {},
    media           = {},
    cta             = {},
  } = proposal_v2;

  const set = (section, field, value) =>
    onChange({ ...proposal_v2, [section]: { ...proposal_v2[section], [field]: value } });

  const reorderSections = (fromKey, insertPos) => {
    const cur = [...sectionOrder];
    const fromIdx = cur.indexOf(fromKey);
    if (fromIdx === -1) return;
    cur.splice(fromIdx, 1);
    const target = insertPos > fromIdx ? insertPos - 1 : insertPos;
    cur.splice(target, 0, fromKey);
    setSectionOrder(cur);
    onChange({ ...proposal_v2, section_order: cur });
  };

  const handleDragStart = (e, key) => {
    setDragKey(key);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.4'; }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '';
    dragNodeRef.current = null;
    setDragKey(null);
    setDropInsertPos(null);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const insertPos = e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
    if (insertPos !== dropInsertPos) setDropInsertPos(insertPos);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragKey !== null && dropInsertPos !== null) {
      reorderSections(dragKey, dropInsertPos);
    }
    setDragKey(null);
    setDropInsertPos(null);
  };

  // — Why bullets —
  const whyVisible  = (p.why_bullets || []).length > 0;
  const toggleWhy   = () => set('persuasive', 'why_bullets', whyVisible ? [] : ['']);
  const updateBullet = (i, v) => {
    const arr = [...(p.why_bullets || [])]; arr[i] = v;
    set('persuasive', 'why_bullets', arr);
  };
  const addBullet    = () => (p.why_bullets || []).length < 5 && set('persuasive', 'why_bullets', [...(p.why_bullets || []), '']);
  const removeBullet = i  => set('persuasive', 'why_bullets', (p.why_bullets || []).filter((_, idx) => idx !== i));

  // — Inclusions —
  const incVisible  = (p.inclusions || []).length > 0;
  const toggleInc   = () => set('persuasive', 'inclusions', incVisible ? [] : ['']);
  const updateInc   = (i, v) => {
    const arr = [...(p.inclusions || [])]; arr[i] = v;
    set('persuasive', 'inclusions', arr);
  };
  const addInc      = () => (p.inclusions || []).length < 8 && set('persuasive', 'inclusions', [...(p.inclusions || []), '']);
  const removeInc   = i  => set('persuasive', 'inclusions', (p.inclusions || []).filter((_, idx) => idx !== i));

  const closingVisible = !!p.closing?.trim();
  const toggleClosing  = () => set('persuasive', 'closing', closingVisible ? '' : ' ');

  const SECTION_RENDER = {
    greeting: (idx) => (
      <SectionWrap key="greeting" title="Header & aanhef" mandatory
        section="greeting" regenSection={regenSection} onRegenerate={onRegenerate}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        <div className="p-4 space-y-3">
          <div>
            <label className="label text-xs">Tagline</label>
            <input className="input text-sm" value={p.tagline || ''}
              onChange={e => set('persuasive', 'tagline', e.target.value)}
              placeholder="Een evocatieve tagline..." />
          </div>
          <div>
            <label className="label text-xs">Aanhef</label>
            <input className="input text-sm" value={p.greeting || ''}
              onChange={e => set('persuasive', 'greeting', e.target.value)}
              placeholder="Beste Familie Janssen," />
          </div>
        </div>
      </SectionWrap>
    ),
    travel_data: (idx) => (
      <SectionWrap key="travel_data" title="Reisdetails" mandatory
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2.5">
            {[
              ['destination',       'Bestemming'],
              ['hotel_name',        'Hotel'],
              ['hotel_stars',       'Sterren'],
              ['room_type',         'Kamertype'],
              ['departure_date',    'Vertrekdatum'],
              ['return_date',       'Terugkomstdatum'],
              ['duration_nights',   'Nachten'],
              ['departure_airport', 'Luchthaven'],
              ['arrival_city',      'Aankomststad'],
              ['board_type',        'Catering'],
              ['flight_info',       'Vluchtinfo'],
              ['price_per_person',  'Prijs p.p. (€)'],
              ['price_total',       'Totaalprijs (€)'],
            ].map(([field, label]) => (
              <div key={field}>
                <label className="label text-xs">{label}</label>
                <input className="input text-sm" value={td[field] ?? ''}
                  onChange={e => set('travel_data', field, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </SectionWrap>
    ),
    photo: (idx) => (
      <SectionWrap key="photo" title="Foto"
        visible={!!media.photo_visible}
        onToggle={() => set('media', 'photo_visible', !media.photo_visible)}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        {media.photo_visible && (
          <PhotoUploadSection
            url={media.photo_url || ''}
            onChange={url => set('media', 'photo_url', url)}
          />
        )}
      </SectionWrap>
    ),
    why: (idx) => (
      <SectionWrap key="why" title="Waarom deze reis?"
        visible={whyVisible} onToggle={toggleWhy}
        section="why" regenSection={regenSection} onRegenerate={onRegenerate}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        <div className="p-4 space-y-3">
          <div>
            <label className="label text-xs">Introductie</label>
            <input className="input text-sm" value={p.why_intro || ''}
              onChange={e => set('persuasive', 'why_intro', e.target.value)}
              placeholder="Korte intro voor deze sectie..." />
          </div>
          <div>
            <label className="label text-xs">Redenen (max 5)</label>
            <div className="space-y-1.5 mb-1.5">
              {(p.why_bullets || []).map((b, i) => (
                <div key={i} className="flex gap-2">
                  <input className="input text-sm flex-1" value={b}
                    onChange={e => updateBullet(i, e.target.value)}
                    placeholder={`Reden ${i + 1}...`} />
                  <button type="button" onClick={() => removeBullet(i)}
                    className="text-gray-400 hover:text-red-500 text-sm px-1.5 transition-colors">×</button>
                </div>
              ))}
            </div>
            {(p.why_bullets || []).length < 5 && (
              <button type="button" onClick={addBullet}
                className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
                + Reden toevoegen
              </button>
            )}
          </div>
        </div>
      </SectionWrap>
    ),
    inclusions: (idx) => (
      <SectionWrap key="inclusions" title="Inbegrepen"
        visible={incVisible} onToggle={toggleInc}
        section="inclusions" regenSection={regenSection} onRegenerate={onRegenerate}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        <div className="p-4">
          <label className="label text-xs">Items (max 8)</label>
          <div className="space-y-1.5 mb-1.5">
            {(p.inclusions || []).map((inc, i) => (
              <div key={i} className="flex gap-2">
                <input className="input text-sm flex-1" value={inc}
                  onChange={e => updateInc(i, e.target.value)}
                  placeholder={`Item ${i + 1}...`} />
                <button type="button" onClick={() => removeInc(i)}
                  className="text-gray-400 hover:text-red-500 text-sm px-1.5 transition-colors">×</button>
              </div>
            ))}
          </div>
          {(p.inclusions || []).length < 8 && (
            <button type="button" onClick={addInc}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
              + Item toevoegen
            </button>
          )}
        </div>
      </SectionWrap>
    ),
    closing: (idx) => (
      <SectionWrap key="closing" title="Afsluiting"
        visible={closingVisible} onToggle={toggleClosing}
        section="closing" regenSection={regenSection} onRegenerate={onRegenerate}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        {closingVisible && (
          <div className="p-4">
            <textarea className="input text-sm min-h-16" value={p.closing || ''}
              onChange={e => set('persuasive', 'closing', e.target.value)}
              placeholder="Slottekst..." />
          </div>
        )}
      </SectionWrap>
    ),
    cta: (idx) => (
      <SectionWrap key="cta" title="Call-to-action"
        visible={!!cta.visible}
        onToggle={() => set('cta', 'visible', !cta.visible)}
        dragHandleProps={{ onMouseDown: e => e.stopPropagation() }}>
        {cta.visible && (
          <div className="p-4 space-y-2.5">
            <div>
              <label className="label text-xs">Tekst</label>
              <input className="input text-sm" value={cta.text || ''}
                onChange={e => set('cta', 'text', e.target.value)}
                placeholder="Plan een afspraak" />
            </div>
            <div>
              <label className="label text-xs">URL (optioneel)</label>
              <input className="input text-sm" value={cta.url || ''}
                onChange={e => set('cta', 'url', e.target.value)}
                placeholder="https://..." />
            </div>
            <div>
              <label className="label text-xs">Opmerking (optioneel)</label>
              <input className="input text-sm" value={cta.note || ''}
                onChange={e => set('cta', 'note', e.target.value)}
                placeholder="Vrijblijvend adviesgesprek" />
            </div>
          </div>
        )}
      </SectionWrap>
    ),
    ai: (idx) => onAiEdit ? <AiEditPanel key="ai" onAiEdit={onAiEdit} /> : null,
  };

  return (
    <div className="space-y-0" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {sectionOrder.map((key, idx) => {
        const renderFn = SECTION_RENDER[key];
        if (!renderFn) return null;
        const isDragging = dragKey === key;
        const showAbove = dropInsertPos === idx && dragKey !== null && dragKey !== key;
        const showBelow = dropInsertPos === idx + 1 && dragKey !== null && dragKey !== key
          && (idx === sectionOrder.length - 1 || sectionOrder[idx + 1] !== dragKey);

        return (
          <div
            key={key}
            draggable
            onDragStart={e => handleDragStart(e, key)}
            onDragEnd={handleDragEnd}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={e => handleDrop(e, idx)}
            style={{ opacity: isDragging ? 0.4 : 1, marginBottom: 10 }}
          >
            {showAbove && <DropIndicator />}
            {renderFn(idx)}
            {showBelow && <DropIndicator />}
          </div>
        );
      })}
    </div>
  );
}
